import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { File } from '../../model/file';
import { FileRules } from '../../model/file.rules';
import { FilesService } from '../../service/files.service';

@Component({
  selector: 'app-file-pill',
  templateUrl: './file-pill.component.html',
  styleUrls: ['./file-pill.component.scss'],
  // OnPush pra evitar ExpressionChangedAfterItHasBeenCheckedError
  // originado no `[disabled]` do botão de delete. O erro vinha de o
  // parent avaliar o input `file` (método `findUploadedFile()`) em
  // duas passes consecutivos e retornar valores diferentes por
  // instabilidade transitória durante a carga inicial, causando o
  // `*ngIf="file; else missing"` a flipar entre variante normal (com
  // `[disabled]="trackFileClosed"`) e variante missing (com
  // `[disabled]="true"`). Com OnPush, a CD interna do pill só reroda
  // quando um input muda por referência, evitando o double-check.
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilePillComponent {
  constructor(private filesService: FilesService) {}

  /** Baixa PELA API, com o token no cabeçalho.
   *
   *  Antes era `<a [href]="file.url">`, que apenas NAVEGAVA para o endereço
   *  da biblioteca de criptografia — feito para autenticação por cookie de
   *  sessão, que esta aplicação não usa. O resultado era 404 (o endereço é
   *  montado com uma barra a mais) e, mesmo corrigido, seria 403: um `<a>`
   *  não envia cabeçalho de autorização. Issue #2376.
   */
  baixar(): void {
    if (!this.file) { return; }
    this.filesService.downloadToDisk(this.file).subscribe(() => {}, () => {});
  }

  // Regra do arquivo (label + flag required + closed).
  // Setter em vez de propriedade simples: snapshota `trackFile.closed`
  // no momento da atribuição. O getter da model chama `dayjs()` a cada
  // leitura ([FileRules.closed]), o Angular lê o binding 2x por CD pass
  // (update + `checkNoChanges` em dev-mode). Snapshotar aqui garante
  // valor imutável DURANTE a vida do binding — evita
  // ExpressionChangedAfterItHasBeenCheckedError. Bônus: setter roda
  // ANTES de qualquer template evaluation, então trackFileClosed já
  // está correto na primeira leitura.
  private _trackFile: FileRules;
  public trackFileClosed = false;

  @Input()
  public get trackFile(): FileRules { return this._trackFile; }
  public set trackFile(value: FileRules) {
    this._trackFile = value;
    this.trackFileClosed = !!(value && value.closed);
  }

  // Arquivo enviado. `null/undefined` → renderiza variante "ausente".
  @Input() file: File | null = null;

  // Inputs para o `<app-file-upload>` interno (quando habilitado).
  @Input() submissionId: number;
  @Input() formGroupController: FormGroup;
  @Input() formName: string;

  // Toggles de funcionalidade (subm. usa upload+delete; rev. só download).
  @Input() showUpload = false;
  @Input() showDelete = false;
  @Input() userIsAuthor = false;
  @Input() userIsChair = false;

  // Habilita o indicador de verificação de similaridade (Docoloc) — configuração
  // por evento (`EventReviewsConfiguration.similarityVerification`).
  @Input() similarityVerification = false;

  // Output: emitido pelo `<app-file-upload>` ao terminar.
  @Output() uploadComplete = new EventEmitter<void>();
  // Output: emitido ao clicar no botão de delete (parent decide o que fazer:
  // dialog de confirmação, chamada ao service, refresh, etc.).
  @Output() deleteFile = new EventEmitter<File>();
  // Outputs de similaridade: parent decide a chamada ao service (submeter
  // verificação / baixar relatório).
  @Output() checkSimilarity = new EventEmitter<File>();
  @Output() downloadSimilarityReport = new EventEmitter<File>();

  @ViewChild('nameEl', { static: false }) nameEl?: ElementRef<HTMLElement>;

  // Mapa de extensão → classe Phosphor (regular outline). Mesma tabela
  // que estava duplicada em `reviews.component.ts` e `submissions.component.ts`.
  public getFileIconClass(): string {
    const f: any = this.file;
    const name = (f?.name || f?.file || '').toString().toLowerCase();
    const ext = name.split('.').pop() || '';
    const map: Record<string, string> = {
      pdf: 'file-pdf',
      doc: 'file-doc', docx: 'file-doc',
      odt: 'file-text', rtf: 'file-text', txt: 'file-text', ps: 'file-text',
      zip: 'file-zip', rar: 'file-zip', '7z': 'file-zip', tar: 'file-zip', gz: 'file-zip',
      mp4: 'file-video', mpg: 'file-video', mov: 'file-video', avi: 'file-video', mkv: 'file-video',
      html: 'file-html', htm: 'file-html',
      jpg: 'file-jpg', jpeg: 'file-jpg',
      png: 'file-png', svg: 'file-svg',
      csv: 'file-csv',
      xls: 'file-xls', xlsx: 'file-xls',
    };
    return `ph ph-${map[ext] || 'file'}`;
  }

  // True quando o nome no pill foi truncado (excede largura). Usado em
  // `[matTooltipDisabled]` para só ativar tooltip nesse caso.
  public isNameOverflowing(): boolean {
    const el = this.nameEl?.nativeElement;
    if (!el) { return false; }
    return el.scrollWidth > el.clientWidth;
  }

  public onDeleteClick(event: Event): void {
    event.stopPropagation();
    if (this.file) {
      this.deleteFile.emit(this.file);
    }
  }

  public onUploadComplete(): void {
    this.uploadComplete.emit();
  }

  public onCheckSimilarityClick(event: Event): void {
    event.stopPropagation();
    if (this.file) {
      this.checkSimilarity.emit(this.file);
    }
  }

  public onDownloadSimilarityReportClick(event: Event): void {
    event.stopPropagation();
    if (this.file) {
      this.downloadSimilarityReport.emit(this.file);
    }
  }
}
